import { Injectable } from '@angular/core';
import { ActionTypeEnum, TreeViewItem } from '@core/components/fc-tree-view/fc-tree.types';
import { Equipment, RecurrenceLogMappedLinks, RecurrenceSnapshot } from '@core/interfaces';
import { LocationEnvironment } from '@core/interfaces/locations-environments';
import { LocationSector } from '@core/interfaces/locations-sectors';
import { sortArchivedThenByName } from '@core/utils/sort-archived-then-by-name';
import { TranslateService } from '@ngx-translate/core';
import { chain, clone } from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class CreateRecurrenceLinkLogsService {
  constructor(private readonly translateService: TranslateService) { }

  public generateLinks = (snapshot: RecurrenceSnapshot): RecurrenceLogMappedLinks => {
    const equipments = clone(snapshot.equipments || []);
    const unlinkedEquipments = equipments.filter((eq) => !eq.locationSector && !eq.locationEnvironment).sort(sortArchivedThenByName);

    const environments = chain(snapshot.locationEnvironments || [])
      .concat(equipments.filter((eq) => eq.locationEnvironment).map((equipment) => ({
        ...equipment.locationEnvironment,
        locationSector: equipment.locationSector,
      })))
      .map((environment) => ({
        ...environment,
        equipments: equipments.filter((eq) => eq.locationEnvironment && eq.locationEnvironment.id === environment.id).sort(sortArchivedThenByName)
      }))
      .uniqBy('id')
      .value();

    const sectors = chain(snapshot.locationSectors || [])
      .concat((snapshot.locationEnvironments || []).map((environment) => environment.locationSector))
      .concat(equipments.filter((eq) => eq.locationSector).map((equipment) => equipment.locationSector))
      .map((sector) => ({
        ...sector,
        locationEnvironments: environments.filter((env) => env.locationSector.id === sector.id).sort(sortArchivedThenByName),
        equipments: equipments.filter((eq) => eq.locationSector && eq.locationSector.id === sector.id && !eq.locationEnvironment).sort(sortArchivedThenByName),
      }))
      .uniqBy('id')
      .sort(sortArchivedThenByName)
      .value();

    return {
      sectors: this.formatSectors(sectors),
      equipmentsWithoutSector: this.formatUnlinkedEquipments(unlinkedEquipments),
    };
  }

  private formatSectors(sectors: LocationSector[]): TreeViewItem[] {
    return (sectors || []).map((sector) => {
      const sectorItem: TreeViewItem = {
        id: sector.id,
        text: sector.name,
        description: sector.description,
        icon: 'sector',
        metadata: 'sector',
        archived: sector.archived,
        archivedText: this.translateService.instant('RECURRENCES.DIALOG.TABS.HISTORIC.ARCHIVED'),
        action: {
          type: ActionTypeEnum.none,
        }
      };

      sectorItem.children = [
        ...this.formatEnvironments(sector.locationEnvironments),
        ...this.formatEquipments(sector.equipments),
      ];

      return sectorItem;
    });
  }

  public formatUnlinkedEquipments(unlinkedEquipments: Equipment[]): TreeViewItem[] {
    const formattedEquipments = this.formatEquipments(unlinkedEquipments);

    if (!formattedEquipments.length) {
      return [];
    }

    return [{
      text: this.translateService.instant('RECURRENCES.DIALOG.TABS.TAB_LINKS.EQUIPMENTS_WITHOUT_SECTOR_ENVIRONMENT'),
      icon: 'more_vertical',
      children: formattedEquipments,
      action: {
        type: ActionTypeEnum.none
      }
    }];
  }

  private formatEnvironments(environments: LocationEnvironment[]): TreeViewItem[] {
    return (environments || []).map((environment) => {
      const environmentItem: TreeViewItem = {
        id: environment.id,
        text: environment.name,
        description: environment.description,
        icon: 'environment',
        archived: environment.archived,
        archivedText: this.translateService.instant('RECURRENCES.DIALOG.TABS.HISTORIC.ARCHIVED'),
        metadata: 'environment',
        action: {
          type: ActionTypeEnum.none
        }
      };

      environmentItem.children = this.formatEquipments(environment.equipments);
      return environmentItem;
    });
  }

  private formatEquipments(equipments: Equipment[]): TreeViewItem[] {
    return (equipments || []).map((equipment) => ({
      id: equipment.id,
      text: equipment.name,
      description: equipment.number,
      archived: equipment.archived,
      archivedText: this.translateService.instant('RECURRENCES.DIALOG.TABS.HISTORIC.ARCHIVED'),
      icon: 'equipment',
      image: equipment.imageUrl,
      children: [],
      metadata: 'equipment',
      action: {
        type: ActionTypeEnum.none
      }
    }));
  }
}
